import { DEFAULT_TIMEZONE } from '@yoyoclub/shared';
import { useCallback, useEffect, useState } from 'react';

import { waitlistApi, type AdminWaitlistEntry } from '../../services/waitlistApi';

interface SessionWaitlistModalProps {
  sessionId: string;
  activityTitle: string;
  sessionDate: string;
  getToken: () => Promise<string | undefined>;
  onClose: () => void;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    timeZone: DEFAULT_TIMEZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function SessionWaitlistModal({
  sessionId,
  activityTitle,
  sessionDate,
  getToken,
  onClose,
}: SessionWaitlistModalProps) {
  const [entries, setEntries] = useState<AdminWaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      if (!token) throw new Error('No auth token');
      const result = await waitlistApi.adminListForSession(token, sessionId);
      setEntries(result?.entries ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  }, [sessionId, getToken]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{activityTitle}</h2>
            <p className="text-sm text-gray-500">{sessionDate} 候补名单</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-2xl font-bold text-primary leading-none">{entries.length}</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wide mt-0.5">
                on waitlist
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64 text-red-600">{error}</div>
          ) : entries.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No one is on the waitlist for this session.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="px-4 py-3 text-left">#</th>
                    <th className="px-4 py-3 text-left">User</th>
                    <th className="px-4 py-3 text-left">Children</th>
                    <th className="px-4 py-3 text-left">Adults</th>
                    <th className="px-4 py-3 text-left">Demand</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700">{entry.position + 1}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{entry.user.name || '—'}</div>
                        <div className="text-xs text-gray-500">{entry.user.email}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{entry.childrenCount}</td>
                      <td className="px-4 py-3 text-gray-700">{entry.adultsCount}</td>
                      <td className="px-4 py-3 text-gray-700">{entry.capacityDemand}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={entry.status} />
                        {entry.status === 'NOTIFIED' && entry.notifiedAt && (
                          <div className="text-xs text-gray-500 mt-1">
                            {formatDateTime(entry.notifiedAt)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{formatDateTime(entry.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string }> = {
    WAITING: { bg: 'bg-blue-100', text: 'text-blue-800' },
    NOTIFIED: { bg: 'bg-amber-100', text: 'text-amber-800' },
    EXPIRED: { bg: 'bg-gray-100', text: 'text-gray-700' },
  };
  const c = config[status] || config.WAITING;
  return (
    <span
      className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${c.bg} ${c.text}`}
    >
      {status}
    </span>
  );
}
